import { Directive, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';

type Rgb = [number, number, number];

interface Rocket {
  x: number;
  y: number;
  vx: number;
  vy: number;
  burstAtY: number;
  color: Rgb;
  trail: Array<[number, number]>;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  decay: number;
  color: Rgb;
  size: number;
  twinkle?: boolean;
  flash?: boolean;
}

const PALETTE: Rgb[] = [
  [245, 158, 11], [249, 115, 22], [255, 230, 150], [165, 192, 255],
  [139, 92, 246], [239, 68, 68], [16, 185, 129], [255, 120, 200],
];
const FLASH_COLOR: Rgb = [255, 240, 200];

/**
 * Background fireworks (Diwali crackers) drawn on a canvas.
 * The canvas should cover the visible screen (position: fixed; inset: 0) so rockets start at the
 * bottom of what the user sees. Runs outside Angular, so the animation never triggers change detection.
 * Movement is time-based: same speed at any frame rate.
 * Usage: <canvas ipmFireworks ignoreSelector=".card"></canvas>, clicking empty background launches a rocket.
 */
@Directive({ selector: 'canvas[ipmFireworks]', standalone: true })
export class FireworksDirective implements OnInit, OnDestroy {
  /** Clicks on these elements do not launch a rocket */
  @Input() ignoreSelector: string = 'button, a, input, label';

  private ctx: CanvasRenderingContext2D | null = null;
  private host: HTMLElement | null = null;
  private width = 0;
  private height = 0;
  private rockets: Rocket[] = [];
  private sparks: Spark[] = [];
  private frameId = 0;
  private lastFrameAt = 0;
  private nextLaunchAt = 0;
  private timers: Array<ReturnType<typeof setTimeout>> = [];
  private resizeObserver: ResizeObserver | null = null;

  constructor(private canvasRef: ElementRef<HTMLCanvasElement>, private ngZone: NgZone) {}

  ngOnInit(): void {
    const canvas = this.canvasRef.nativeElement;
    this.host = canvas.parentElement;
    this.ctx = canvas.getContext('2d');
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!this.host || !this.ctx || prefersReducedMotion) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.resize();
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this.canvasRef.nativeElement);
      this.host!.addEventListener('pointerdown', this.onPointerDown);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.launch(this.width * 0.3);
      this.later(() => this.launch(this.width * 0.7), 400);
      this.frameId = requestAnimationFrame(this.frame);
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frameId);
    this.timers.forEach(timer => clearTimeout(timer));
    this.resizeObserver?.disconnect();
    this.host?.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.rockets = [];
    this.sparks = [];
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest(this.ignoreSelector)) {
      return;
    }
    const rect = this.canvasRef.nativeElement.getBoundingClientRect();
    this.launch(event.clientX - rect.left);
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      this.rockets = [];
      this.sparks = [];
      this.lastFrameAt = 0;
    }
  };

  private resize(): void {
    const canvas = this.canvasRef.nativeElement;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = canvas.clientWidth;
    this.height = canvas.clientHeight;
    canvas.width = this.width * dpr;
    canvas.height = this.height * dpr;
    this.ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private later(callback: () => void, delay: number): void {
    this.timers.push(setTimeout(callback, delay));
  }

  private random(min: number, max: number): number {
    return min + Math.random() * (max - min);
  }

  private randomColor(): Rgb {
    return PALETTE[Math.floor(Math.random() * PALETTE.length)];
  }

  private launch(x: number = this.random(this.width * 0.1, this.width * 0.9)): void {
    this.rockets.push({
      x,
      y: this.height + 10,
      vx: this.random(-0.6, 0.6),
      vy: -this.random(this.height * 0.011, this.height * 0.0145),
      burstAtY: this.random(this.height * 0.12, this.height * 0.5),
      color: this.randomColor(),
      trail: [],
    });
  }

  private burst(x: number, y: number, color: Rgb): void {
    const shape = Math.random();
    const count = Math.floor(this.random(70, 120));
    const secondColor = this.randomColor();
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      let speed = this.random(1.5, 5);
      if (shape < 0.35) {
        speed = 4.2; // ring
      } else if (shape < 0.5) {
        speed = i % 2 ? 2.2 : 4.6; // double ring
      }
      this.sparks.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        decay: this.random(0.008, 0.018),
        color: Math.random() < 0.35 ? secondColor : color,
        size: this.random(1.2, 2.4),
        twinkle: Math.random() < 0.25,
      });
    }
    // crackle flash
    this.sparks.push({ x, y, vx: 0, vy: 0, life: 1, decay: 0.12, color: FLASH_COLOR, size: 26, flash: true });
  }

  private readonly frame = (time: number): void => {
    const ctx = this.ctx!;
    // frame step relative to 60fps (1 = one 16.7ms frame), capped after tab switches / long frames
    const step = this.lastFrameAt ? Math.min((time - this.lastFrameAt) / (1000 / 60), 3) : 1;
    this.lastFrameAt = time;
    // fade the previous frame to leave short trails
    ctx.globalCompositeOperation = 'destination-out';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.fillRect(0, 0, this.width, this.height);
    ctx.globalCompositeOperation = 'lighter';

    if (time > this.nextLaunchAt) {
      this.launch();
      if (Math.random() < 0.35) {
        this.later(() => this.launch(), this.random(150, 400));
      }
      this.nextLaunchAt = time + this.random(500, 1400);
    }

    this.drawRockets(ctx, step);
    this.drawSparks(ctx, time, step);
    this.frameId = requestAnimationFrame(this.frame);
  };

  private drawRockets(ctx: CanvasRenderingContext2D, step: number): void {
    const drag = Math.pow(0.985, step);
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const rocket = this.rockets[i];
      rocket.x += rocket.vx * step;
      rocket.y += rocket.vy * step;
      rocket.vy *= drag;
      rocket.trail.push([rocket.x, rocket.y]);
      if (rocket.trail.length > 8) {
        rocket.trail.shift();
      }
      const [r, g, b] = rocket.color;
      ctx.strokeStyle = `rgba(${r},${g},${b},0.7)`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      rocket.trail.forEach(([px, py], index) => (index ? ctx.lineTo(px, py) : ctx.moveTo(px, py)));
      ctx.stroke();
      if (rocket.y <= rocket.burstAtY || rocket.vy > -1.2) {
        this.burst(rocket.x, rocket.y, rocket.color);
        this.rockets.splice(i, 1);
      }
    }
  }

  private drawSparks(ctx: CanvasRenderingContext2D, time: number, step: number): void {
    const drag = Math.pow(0.985, step);
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      const [r, g, b] = spark.color;
      if (spark.flash) {
        const glow = ctx.createRadialGradient(spark.x, spark.y, 0, spark.x, spark.y, spark.size * spark.life * 2);
        glow.addColorStop(0, `rgba(${r},${g},${b},${0.5 * spark.life})`);
        glow.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(spark.x, spark.y, spark.size * 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        spark.vx *= drag;
        spark.vy = spark.vy * drag + 0.035 * step; // gravity
        spark.x += spark.vx * step;
        spark.y += spark.vy * step;
        const alpha = spark.life * (spark.twinkle ? 0.5 + 0.5 * Math.sin(time / 40 + i) : 1);
        ctx.fillStyle = `rgba(${r},${g},${b},${Math.max(alpha, 0) * 0.9})`;
        ctx.beginPath();
        ctx.arc(spark.x, spark.y, spark.size * (0.4 + spark.life * 0.6), 0, Math.PI * 2);
        ctx.fill();
      }
      spark.life -= spark.decay * step;
      if (spark.life <= 0) {
        this.sparks.splice(i, 1);
      }
    }
  }
}
